/**
 * zeroDecimalCurrencies.ts — WEB-1607.
 *
 * Валюты, у которых наименьшая единица — сама валюта, а не её сотая доля.
 * Stripe хранит суммы в наименьших единицах: у доллара это цент (2997 → $29.97),
 * а у иены — сама иена (1000 → ¥1000, а НЕ ¥10.00).
 *
 * ⚠ ЭТО КОПИЯ. Настоящий справочник —
 * `STRIPE_ZERO_DECIMAL_CURRENCIES` в `backend/src/stripe/services/stripe.service.ts`.
 *
 * Почему копия, а не запрос к бэку: пейволл — страница покупки, и цена на ней
 * обязана нарисоваться из тех данных, которые уже приехали вместе с пейволлом.
 * Отдельный поход за справочником добавил бы к показу цены ещё одну точку
 * отказа: сорвался запрос — покупатель видит либо пустоту, либо неверную цену.
 * Списку же полтора десятка строк, и меняется он раз в несколько лет.
 *
 * Копия держится сторожем паритета — `__tests__/zeroDecimalCurrencies.parity.test.ts`
 * читает файл бэка и сравнивает множества. Правка одной стороны без другой
 * красит его в красный. Не удаляй сторож — без него это просто разъезжающиеся
 * списки, а расходятся они молча, показывая покупателю и владельцу разные цены.
 *
 * Крона ISK намеренно ВНЕ списка: Stripe требует представлять её как
 * двухдесятичную (5 крон = amount 500), то есть деление на сто для неё верно.
 */
export const STRIPE_ZERO_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  "bif",
  "clp",
  "djf",
  "gnf",
  "jpy",
  "kmf",
  "krw",
  "mga",
  "pyg",
  "rwf",
  "ugx",
  "vnd",
  "vuv",
  "xaf",
  "xof",
  "xpf",
]);

/**
 * WEB-2655 — валюты с ТРЕМЯ знаками после запятой (список Stripe «Three-decimal
 * currencies»): бахрейнский, иорданский, кувейтский динар, оманский риал,
 * тунисский динар. Наименьшая единица у них — тысячная доля: 19990 значит
 * 19.990 KWD. Пока их здесь не было, кабинет делил такую сумму на сто и
 * показывал 199.90 KWD — в десять раз больше; обратный перевод при вводе
 * цены дал бы сумму в десять раз меньше.
 *
 * Это копия серверного `STRIPE_THREE_DECIMAL_CURRENCIES`
 * (`backend/src/stripe/utils/money-display.util.ts`, WEB-2669): показ выручки и
 * её SQL на сервере читают тот же список из пяти кодов. Совпадение двух списков
 * держит сторож паритета (`__tests__/zeroDecimalCurrencies.parity.test.ts`) —
 * он краснеет, если списки разошлись. Чего список пока НЕ покрывает: потолки
 * серверных списаний и отправка сумм в рекламные сети его не читают (они всё
 * ещё делят на сто) — это WEB-2408 п.3, упирается в большой файл сервиса Stripe.
 */
export const STRIPE_THREE_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  "bhd",
  "jod",
  "kwd",
  "omr",
  "tnd",
]);

/** Валюта без дробной части? Регистронезависимо; пустой код — «нет». */
export function isZeroDecimalCurrency(currency?: string | null): boolean {
  if (!currency) return false;
  return STRIPE_ZERO_DECIMAL_CURRENCIES.has(currency.toLowerCase());
}

/**
 * WEB-2655 — сколько знаков после запятой у валюты: 0 (иена), 3 (динар) или 2
 * (все остальные и неизвестная валюта — прежнее правило).
 *
 * ЕДИНСТВЕННОЕ место, где решается число знаков для сумм Stripe. Читатели
 * (форматтеры цены, выручки, выгрузок) раньше решали это сами — «иена 0,
 * остальные 2» — и потому все вместе промахнулись мимо динара. Прямой вызов
 * `isZeroDecimalCurrency` вне этого файла разрешён только формам потолков
 * серверных списаний (у них своя мера — сотые главной единицы, см.
 * utils/chargeLimitUnits.ts); список держит сторож
 * `__tests__/currencyFractionDigits.ends.web2655.test.ts`.
 */
export function currencyFractionDigits(currency?: string | null): 0 | 2 | 3 {
  if (isZeroDecimalCurrency(currency)) return 0;
  if (currency && STRIPE_THREE_DECIMAL_CURRENCIES.has(currency.toLowerCase())) {
    return 3;
  }
  return 2;
}

/** Сколько наименьших единиц в одной отображаемой: 1, 100 или 1000. */
export function minorUnitsPerDisplayUnit(
  currency?: string | null,
): 1 | 100 | 1000 {
  return (10 ** currencyFractionDigits(currency)) as 1 | 100 | 1000;
}

/**
 * WEB-1626 — наименьшие единицы → сумма для человека, ОДНИМ правилом.
 *
 * Зеркало бэкендовой `minorUnitsToDisplayAmount` (stripe/utils/
 * money-display.util.ts). Появилась потому, что деление на сто было записано
 * на фронте по месту в нескольких файлах (цена пейволла, когорты, LTV) — и в
 * когортах оно осталось безусловным даже после WEB-1607, из-за чего японский
 * проект видел LTV в сто раз меньше настоящего.
 *
 * Валюта неизвестна (не передана или null) — делим на сто, как раньше:
 * множителя, верного сразу для иен и долларов, не существует, а выдумать его
 * значило бы соврать про часть денег.
 */
export function minorUnitsToDisplayAmount(
  minorUnits: number,
  currency?: string | null,
): number {
  // WEB-2655: делитель — по числу знаков валюты (у динара 1000), не «1 или 100».
  return minorUnits / minorUnitsPerDisplayUnit(currency);
}

/**
 * WEB-1469 — обратный перевод: сумма для человека → наименьшие единицы.
 *
 * Зеркало бэкендовой `displayAmountToMinorUnits` (stripe/utils/
 * money-display.util.ts). Понадобилась продлению доменов: сохранённая цена
 * приходит В ВАЛЮТЕ (`renewalCost: 9.99`), а сравнение с балансом и показ идут
 * в наименьших единицах. Записанное по месту «* 100» для иены выдумывало сумму
 * в сто раз больше настоящей — и не только на экране: тем же раздутым числом
 * сравнивался баланс, то есть продление могло заблокироваться при достаточных
 * деньгах.
 *
 * Округление обязательно: `29.97 * 100` в двоичной арифметике даёт
 * 2996.9999999999995, и без округления копейка терялась бы при каждом переводе
 * (та же причина, что у бэкендового двойника).
 *
 * Валюта неизвестна — умножаем на сто, как раньше: множителя, верного сразу
 * для иен и долларов, не существует.
 */
export function displayAmountToMinorUnits(
  amount: number,
  currency?: string | null,
): number {
  // WEB-2655: множитель — тот же, что у прямого перевода (у динара 1000).
  return Math.round(amount * minorUnitsPerDisplayUnit(currency));
}
